import React from "react";
import '../build/big-manager.min.js';
import { useRef, useEffect, useState, useImperativeHandle, forwardRef, useCallback } from "react";

const CSS = {
	common: {"position":"relative","margin":"0","padding":"0","display":"block","WebkitUserSelect":"none","overflow":"hidden","touchAction":"none"},
	nomouse: {"MozUserSelect":"none","MsUserSelect":"none","userSelect":"none","WebkitTouchCallout":"none","MozTouchCallout":"none","MsTouchCallout":"none","touchCallout":"none","pointerEvents":"none","WebkitPointerEvents":"none","MozPointerEvents":"none","MsPointerEvents":"none"},
	grabbing: {"cursor":'grabbing'},
	ungrabbing: {"cursor":'grab'},
	spinner: {"boxSizing":"border-box","position":"absolute","top":"50%","left":"50%","borderRadius":"50%","borderStyle":"solid","transition":"transform 60s","transform":"rotate(0deg)"},
	spinner_spinning: {"transform":"rotate(36000deg)"}
}



export default forwardRef(({
	value,
	onInfos,
	onChange = false,// optional, used by the move() function to push the state up
	camera_animation_duration = 1,
	mouse_control = true,
	disable_spinner = false,
	spinner_settings = {},
	cursor_url=false,//must be square for correct centering
	cursor_size=32,
	children
},ref)=>{


	// ------- General -------
	const settings = value;
	const [is_grabbing,setIsGrabbing] = useState(false);
	const [is_grabbing_unholded,setIsGrabbingUnholded] = useState(false);
	const [big,setBIG] = useState(false);
	const [internal_settings,setInternalSettings] = useState();//We use internal settings because we should not change parents data when we rotate the camera thru the mouse
	const supplemental_CSS = {};

	// ------- Creation, loading and destruction -------
	const elem_ref = useCallback(node => {
		if(node !== null){
			setBIG((big)=>{
				if(big){
					return big;
				}
				if(!window.BIGManaged){
					// Cover a problem on non webGL device
					return false;
				}
				const new_big = new window.BIGManaged(node,false);
				new_big.updateSettings(internal_settings||{},0);
				return new_big;
			});
		}
	},[]);
	useEffect(()=>{
		return ()=>{
			big&&big.dispose();
		}
	},[]);


	// ------- Download function ------
	// IMPORTANT NOTE
	// WE ARE USING BIGDownload without sugar coating like in the old angluar
	useImperativeHandle(ref,()=>({
		download:(overwrite_settings,download_settings)=>{
			const cloned_big_settings = JSON.parse(
				JSON.stringify(overwrite_settings||internal_settings)
			);

			window.BIGDownload(cloned_big_settings,{
				filename:						download_settings.filename,
				export_3d:						download_settings.export_3d,
				big_managed_instance_to_use: 	download_settings.reuse_big_instance?big:null,
				success_callback: 				download_settings.success_callback,
				error_callback: 				download_settings.error_callback
			});
		}
	}));



	// ------- Spinner -------
	const [spinning,setSpinning] = useState(false);
	const spinner = {
		diameter: 40,
		color1: "#000",
		color2: "#ccc",
		thickness: 2,
		...spinner_settings,
	}
	useEffect(()=>{
		const timeout_id = window.setTimeout(()=>{
			setSpinning(true);
		},10);
		return ()=>{
			window.clearTimeout(timeout_id);
		}
	},[]);



	// ------- settings & camera_animation_duration update -------
	if(settings && settings.renderer &&
		settings.renderer.width && settings.renderer.height){
		supplemental_CSS.width = settings.renderer.width+'px';
		supplemental_CSS.height = settings.renderer.height+'px';
	}
	useEffect(()=>{
		setInternalSettings(JSON.parse(JSON.stringify(settings)));
	},[settings]);
	useEffect(()=>{
		if(big&&internal_settings){
			big.updateSettings(internal_settings,is_grabbing?0:camera_animation_duration);
			if(!is_grabbing){
				onInfos&&onInfos(big.infos);
			}
		}
	},[big,internal_settings,camera_animation_duration,is_grabbing,onInfos]);



	// ------- Camera control -------
	useEffect(()=>{
		if(!mouse_control&&is_grabbing){
			ungrab(false);
		}
	},[mouse_control,is_grabbing]);


	const [last_coordinates,setLastCoordinates] = useState(false);
	function ungrab(event){
		if(event){
			event.preventDefault();
		}
		// console.log('ungrab');
		setIsGrabbing(false);
		setLastCoordinates(false);
		setIsGrabbingUnholded(false);
	};
	function ungrabWithClickCatch(event){
		if(event){
			event.preventDefault();
		}
		if(last_coordinates || is_grabbing_unholded){//ungrab only if it moved
			ungrab(event);
		}else{
			setIsGrabbingUnholded(true);
		}
	}
	function grab(event){
		if(event){
			event.preventDefault();
		}
		// console.log('grab');
		if(!is_grabbing_unholded){
			setIsGrabbing(true);
			setLastCoordinates(false);
		}
	};
	function move(event){
		event.preventDefault();
		// console.log('move',is_grabbing_unholded,is_grabbing);
		if(!mouse_control || (!is_grabbing_unholded && !is_grabbing) || !internal_settings || !internal_settings.camera){
			return;
		}
		var m_x,m_y;
		if(event.targetTouches && event.targetTouches.length > 0){
			m_x = event.targetTouches[0].clientX;
			m_y = event.targetTouches[0].clientY;
		}else{
			m_x = event.clientX;
			m_y = event.clientY;
		}

		let new_rotation_x = internal_settings.camera.rotation_x;
		let new_rotation_y = internal_settings.camera.rotation_y;
		if(last_coordinates && is_grabbing && internal_settings && internal_settings.renderer.width){

			var x = last_coordinates.x - m_x, y = last_coordinates.y - m_y;
			var rot_factor = 100;

			new_rotation_x += x/rot_factor;
			new_rotation_y += y/rot_factor;
		}

		// It is capped inside BIG but it should here too
		if(Number.isFinite(internal_settings.camera.rotation_y_max)){
			new_rotation_y = Math.min(new_rotation_y,internal_settings.camera.rotation_y_max);
		}
		if(Number.isFinite(internal_settings.camera.rotation_y_min)){
			new_rotation_y = Math.max(new_rotation_y,internal_settings.camera.rotation_y_min);
		}


		setLastCoordinates({x:m_x,y:m_y});
		if(typeof onChange === 'function'){
			onChange({
				...internal_settings,
				camera:{
					...internal_settings.camera,
					rotation_x: new_rotation_x,
					rotation_y: new_rotation_y
				}
			});
		}else{
			setInternalSettings((internal_settings)=>({
				...internal_settings,
				camera:{
					...internal_settings.camera,
					rotation_x: new_rotation_x,
					rotation_y: new_rotation_y
				}
			}));
		}

	}

	const cursor_center = Math.round(cursor_size/2);
	const cursor_img = cursor_url?('url('+cursor_url+') '+cursor_center+' '+cursor_center+','):'';
	return React.createElement(
	"div",
	{
		ref: elem_ref,
		style: {
			...CSS.common,
			...(mouse_control?(
				is_grabbing?{cursor:cursor_img+'grabbing'}:{cursor:cursor_img+'grab'}
			):{}),
			...supplemental_CSS
		},
		className: 'big-react',

			onTouchStart:grab,
			onTouchMove:move,
			onTouchCancel:ungrab,
			onTouchEnd:ungrab,
			onMouseDown:grab,
			onMouseMove:move,
			onMouseUp:ungrabWithClickCatch,
			onMouseLeave:ungrab
	},
		[
			// ------- Spinner -------
			(!big)&&React.createElement('div',{
				key:'spinner',
				style:{...CSS.spinner,...{
					'width':spinner.diameter,
					'height':spinner.diameter,
					'marginLeft':(-spinner.diameter/2)+'px',
					'marginTop':(-spinner.diameter/2)+'px',
					'borderColor':spinner.color2,
					'borderTopColor':spinner.color1,
					'borderWidth':spinner.thickness+'px'
				},...(spinning?CSS.spinner_spinning:{})}
			})
		]
	);
})
